import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Upload,
  Download,
  Users,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Printer,
  Eye,
  EyeOff,
  Info
} from 'lucide-react';
import apiService from '@/services/api';

export default function CreateParentAccountsPage() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createdAccounts, setCreatedAccounts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showPINs, setShowPINs] = useState(true);
  const [smsStatus, setSmsStatus] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          selectedFile.type === 'application/vnd.ms-excel' ||
          selectedFile.name.endsWith('.xlsx') ||
          selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setErrors([]);
      } else {
        setErrors(['Please select a valid Excel file (.xlsx or .xls)']);
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setErrors(['Please select a file first']);
      return;
    }

    setLoading(true);
    setErrors([]);
    setCreatedAccounts([]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiService.createParentAccountsBulk(formData);

      setCreatedAccounts(response.created_accounts || []);
      
      // Show SMS status
      if (response.sms_sent !== undefined || response.sms_failed !== undefined) {
        setSmsStatus({
          sent: response.sms_sent || 0,
          failed: response.sms_failed || 0
        });
      }
      
      if (response.errors && response.errors.length > 0) {
        setErrors(response.errors);
      }

    } catch (error) {
      console.error('Error uploading file:', error);
      setErrors([error.message || 'Failed to process file. Please check the format and try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadCSV = () => {
    if (createdAccounts.length === 0) return;

    const headers = ['Student Name', 'Parent Name', 'Phone Number', 'PIN', 'Relationship'];
    const rows = createdAccounts.map(acc => [
      acc.student_name,
      acc.parent_name,
      acc.phone_number,
      acc.pin,
      acc.relationship
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parent_accounts_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadExcelTemplate = () => {
    // Create a sample CSV that can be opened in Excel
    const headers = ['Student Name', 'Parent Name', 'Phone Number', 'Relationship'];
    const sampleData = [
      ['John Doe', 'Mary Doe', '7750001', 'Mother'],
      ['Jane Smith', 'Robert Smith', '7750002', 'Father'],
      ['Peter Jones', 'Sarah Jones', '7750003', 'Guardian']
    ];

    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'parent_accounts_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Parent Accounts</h1>
        <p className="text-gray-600">Bulk import parent accounts from an Excel file</p>
      </div>

      {/* Instructions Card */}
      <Card className="mb-6 print:hidden">
        <CardHeader className="bg-blue-50">
          <CardTitle className="text-lg flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600" />
            Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-sm mb-2">Excel File Format Required:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-2">
                <li>Column A: <strong>Student Name</strong> (exact match with student in database)</li>
                <li>Column B: <strong>Parent Name</strong></li>
                <li>Column C: <strong>Phone Number</strong> (7-10 digits)</li>
                <li>Column D: <strong>Relationship</strong> (Mother/Father/Guardian)</li>
              </ul>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> The system will automatically generate 4-digit PINs for each parent account.
                Make sure to save or print the generated credentials to distribute to parents.
              </p>
            </div>
            <Button onClick={downloadExcelTemplate} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Download Excel Template
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card className="mb-6 print:hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Excel File
          </CardTitle>
          <CardDescription>
            Select an Excel file containing parent information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FileSpreadsheet className="h-10 w-10 text-gray-400 mb-2" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">Excel files (.xlsx, .xls)</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            </div>

            {file && (
              <Alert>
                <FileSpreadsheet className="h-4 w-4" />
                <AlertDescription>
                  <strong>Selected file:</strong> {file.name}
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleUpload}
              disabled={!file || loading}
              className="w-full gap-2"
              size="lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Create Parent Accounts
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Errors */}
      {errors.length > 0 && (
        <Alert variant="destructive" className="mb-6 print:hidden">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-semibold mb-2">Errors occurred during processing:</p>
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, idx) => (
                <li key={idx} className="text-sm">{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Created Accounts */}
      {createdAccounts.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4 print:hidden">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <h2 className="text-xl font-bold">
                Successfully Created {createdAccounts.length} Parent Account{createdAccounts.length !== 1 ? 's' : ''}
              </h2>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowPINs(!showPINs)} variant="outline" size="sm" className="gap-2">
                {showPINs ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showPINs ? 'Hide' : 'Show'} PINs
              </Button>
              <Button onClick={handleDownloadCSV} variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" />
                Download CSV
              </Button>
              <Button onClick={handlePrint} variant="outline" size="sm" className="gap-2">
                <Printer className="h-4 w-4" />
                Print
              </Button>
            </div>
          </div>

          <Alert className="mb-4 bg-amber-50 border-amber-200 print:hidden">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Important:</strong> Please save these credentials securely. Parents will need their phone number and PIN to access the portal.
            </AlertDescription>
          </Alert>

          {smsStatus && (
            <Alert className={`mb-4 print:hidden ${smsStatus.failed > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
              <CheckCircle2 className={`h-4 w-4 ${smsStatus.failed > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
              <AlertDescription className={smsStatus.failed > 0 ? 'text-yellow-800' : 'text-green-800'}>
                <strong>SMS Notifications:</strong> {smsStatus.sent} sent successfully
                {smsStatus.failed > 0 && <span>, {smsStatus.failed} failed</span>}
                {smsStatus.sent === 0 && smsStatus.failed === 0 && <span> (Test mode - no SMS sent)</span>}
              </AlertDescription>
            </Alert>
          )}

          {/* Print Header */}
          <div className="hidden print:block mb-6">
            <h1 className="text-2xl font-bold text-center mb-2">Penama Education Portal</h1>
            <h2 className="text-xl text-center mb-4">Parent Portal Access Credentials</h2>
            <p className="text-center text-sm text-gray-600 mb-2">
              Generated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="text-center text-sm text-gray-600 mb-6">
              Total Accounts: {createdAccounts.length}
            </p>
          </div>

          <Card className="print:shadow-none">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Parent Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PIN
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Relationship
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {createdAccounts.map((account, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {idx + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {account.student_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {account.parent_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                          {account.phone_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {showPINs ? (
                            <span className="font-mono font-bold text-lg text-blue-600">
                              {account.pin}
                            </span>
                          ) : (
                            <span className="text-gray-400">••••</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Badge variant="outline">{account.relationship}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Print Footer */}
          <div className="hidden print:block mt-8 text-center text-sm text-gray-600">
            <p>Parents can access the portal at: https://physics-ia-portal.preview.emergentagent.com/parent/login</p>
            <p className="mt-2">For assistance, please contact the school office.</p>
          </div>
        </>
      )}
    </div>
  );
}
